import { createElement, Fragment, ReactElement, useCallback } from 'react';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import { DEFAULT_LANGUAGE } from 'project.config';
import Skeleton from '../skeleton';
import { TypographyProps } from './types';

const Typography: React.FC<TypographyProps> = ({
  children,
  className = '',
  translation,
  as = 'p',
  asSkeleton = false,
  ...props
}) => {
  const locale = useLocale();

  const { formatMessage } = useFormat({ name: translation?.file });

  const getContent = useCallback(() => {
    // Check if the children has different locales
    if (children !== undefined && typeof children !== 'string') {
      // Update text based on locale
      return children?.[locale] || children?.[DEFAULT_LANGUAGE];
    }

    // Check if there is translation
    if (translation) {
      const content = formatMessage({ id: translation.id, defaultMessage: children });
      return content;
    }

    return children;
  }, [formatMessage, locale, translation, children]);

  // Constructing default props of the element
  const elementProps: ReactElement['props'] = {
    className: `${className} relative`,
    ...props,
  };

  const TypographyElement = createElement(
    as == 'fragment' ? Fragment : as,
    as !== 'fragment' && (elementProps as any),
    <>
      {asSkeleton && <Skeleton />}
      {getContent()}
    </>,
  );

  return TypographyElement;
};

export default Typography;
